import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Search, Activity, Users, Receipt, Loader2, Shield, ArrowRight, Command } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_META = {
    case: { icon: Activity, color: "text-cyan-700 bg-cyan-50", label: "Case" },
    client: { icon: Users, color: "text-indigo-700 bg-indigo-50", label: "Client" },
    invoice: { icon: Receipt, color: "text-emerald-700 bg-emerald-50", label: "Invoice" },
    team: { icon: Shield, color: "text-amber-700 bg-amber-50", label: "Team" },
};

/**
 * CommandPalette — Ctrl+K / Cmd+K opens a Spotlight-style global search dialog.
 * Searches cases, clients, invoices and team via /api/admin/search?q= and lets the
 * user jump to the matching record. Mounted once at Admin root.
 */
export default function CommandPalette() {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [active, setActive] = useState(0);
    const inputRef = useRef(null);
    const nav = useNavigate();

    // Open / close via Ctrl+K | Cmd+K, plus Esc to close.
    useEffect(() => {
        const onKey = (e) => {
            const isMac = navigator.platform.toLowerCase().includes("mac");
            const ctrl = isMac ? e.metaKey : e.ctrlKey;
            if (ctrl && (e.key === "k" || e.key === "K")) {
                e.preventDefault();
                setOpen((o) => !o);
            } else if (e.key === "Escape" && open) {
                setOpen(false);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open]);

    useEffect(() => {
        if (open) {
            setTimeout(() => inputRef.current?.focus(), 50);
            setActive(0);
        } else {
            setQ("");
            setItems([]);
        }
    }, [open]);

    // Debounced search
    useEffect(() => {
        if (!open) return;
        const term = q.trim();
        if (term.length < 2) {
            setItems([]);
            return;
        }
        const handle = setTimeout(async () => {
            setLoading(true);
            try {
                const { data } = await axios.get(`${API}/admin/search`, { params: { q: term } });
                setItems(data.items || []);
                setActive(0);
            } catch (e) {
                setItems([]);
            } finally {
                setLoading(false);
            }
        }, 200);
        return () => clearTimeout(handle);
    }, [q, open]);

    const pick = useCallback((item) => {
        setOpen(false);
        if (item.kind === "case") nav(`/cases/${item.id || item.label}`);
        else if (item.kind === "invoice") nav(`/admin?tab=invoices`);
        else if (item.kind === "client") nav(`/admin?tab=clients`);
        else if (item.kind === "team") nav(`/admin?tab=team`);
    }, [nav]);

    const onKeyDown = (e) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActive((a) => Math.min(items.length - 1, a + 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((a) => Math.max(0, a - 1));
        } else if (e.key === "Enter" && items[active]) {
            e.preventDefault();
            pick(items[active]);
        }
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-[12vh] px-4" data-testid="command-palette">
            <button className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" aria-label="Close" onClick={() => setOpen(false)} />
            <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden animate-[tr-cp-in_180ms_cubic-bezier(.22,1,.36,1)]">
                <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
                    <Search size={16} className="text-slate-400" />
                    <input
                        ref={inputRef}
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        onKeyDown={onKeyDown}
                        placeholder="Search cases, clients, invoices, team…"
                        data-testid="palette-input"
                        className="flex-1 bg-transparent outline-none text-[14px] placeholder-slate-400"
                    />
                    {loading ? <Loader2 size={14} className="animate-spin text-slate-400" /> : (
                        <kbd className="px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 font-mono-tr text-[10.5px] text-slate-500">ESC</kbd>
                    )}
                </div>
                <div className="max-h-[60vh] overflow-y-auto">
                    {q.trim().length < 2 ? (
                        <div className="py-12 text-center text-[13px] text-slate-500">
                            <Command size={18} className="mx-auto text-slate-300 mb-2" />
                            Type at least 2 characters. <span className="font-mono-tr text-[11px]">Ctrl/⌘ + K</span> reopens this dialog anywhere.
                        </div>
                    ) : items.length === 0 && !loading ? (
                        <div className="py-12 text-center text-[13px] text-slate-500">No matches for "<strong className="text-slate-900">{q}</strong>"</div>
                    ) : (
                        <ul className="divide-y divide-slate-100">
                            {items.map((it, idx) => {
                                const meta = KIND_META[it.kind] || KIND_META.case;
                                const Icon = meta.icon;
                                const isActive = idx === active;
                                return (
                                    <li
                                        key={`${it.kind}-${it.id || it.label}-${idx}`}
                                        onClick={() => pick(it)}
                                        onMouseEnter={() => setActive(idx)}
                                        data-testid={`palette-item-${idx}`}
                                        className={`px-4 py-2.5 flex items-center gap-3 cursor-pointer transition ${isActive ? "bg-cyan-50/70" : "hover:bg-slate-50"}`}
                                    >
                                        <div className={`w-7 h-7 rounded-md grid place-items-center ${meta.color} shrink-0`}>
                                            <Icon size={13} />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="text-[13px] text-slate-900 truncate">{it.label}</div>
                                            <div className="text-[11.5px] text-slate-500 truncate">{it.sublabel || meta.label}</div>
                                        </div>
                                        {isActive && <ArrowRight size={13} className="text-slate-400" />}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </div>
            <style>{`@keyframes tr-cp-in{from{opacity:0;transform:translateY(-12px) scale(.97)}to{opacity:1;transform:translateY(0) scale(1)}}`}</style>
        </div>
    );
}
